import React from 'react';
import { motion } from 'framer-motion';
interface CircleVisualProps {
  radius?: number;
  diameter?: number;
  circumference?: number;
  area?: number;
  label?: string;
  showFormula?: boolean;
  highlightRadius?: boolean;
  highlightDiameter?: boolean;
  sectorAngle?: number;
  highlightSector?: boolean;
  mode?: 'stimulus' | 'support';
}
/**
 * Helper: build an SVG arc path for a sector from 0° to `angleDeg`.
 * The sector starts at 12-o'clock (−90°) and sweeps clockwise.
 */
function sectorPath(cx: number, cy: number, r: number, angleDeg: number): string {
  if (angleDeg >= 360) {
    // Full circle — use two arcs
    return `M ${cx} ${cy - r} A ${r} ${r} 0 1 1 ${cx} ${cy + r} A ${r} ${r} 0 1 1 ${cx} ${cy - r} Z`;
  }
  const startRad = -Math.PI / 2; // 12 o'clock
  const endRad = startRad + angleDeg * Math.PI / 180;
  const largeArc = angleDeg > 180 ? 1 : 0;
  const x1 = cx + r * Math.cos(startRad);
  const y1 = cy + r * Math.sin(startRad);
  const x2 = cx + r * Math.cos(endRad);
  const y2 = cy + r * Math.sin(endRad);
  return `M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${largeArc} 1 ${x2} ${y2} Z`;
}
export function CircleVisual({
  radius: propRadius,
  diameter: propDiameter,
  circumference,
  area,
  label,
  showFormula = false,
  highlightRadius = false,
  highlightDiameter = false,
  sectorAngle,
  highlightSector = false,
  mode = 'support'
}: CircleVisualProps) {
  // Calculate missing values
  const radius = propRadius ?? (propDiameter ? propDiameter / 2 : 50);
  const diameter = propDiameter ?? radius * 2;
  // SVG dimensions
  const svgSize = 280;
  const cx = svgSize / 2;
  const cy = svgSize / 2;
  // Scale circle to fill SVG nicely — ensure minimum visible size
  const maxDisplayRadius = svgSize * 0.38; // ~106px
  const minDisplayRadius = 40;
  const maxExpectedRadius = 20;
  const scaleFactor = maxDisplayRadius / maxExpectedRadius;
  const circleRadius = Math.max(minDisplayRadius, Math.min(maxDisplayRadius, radius * scaleFactor));
  const hasSector = typeof sectorAngle === 'number' && sectorAngle > 0 && sectorAngle < 360;
  return <div className="flex flex-col items-center gap-4 py-3" data-id="element-13185" data-mp="1hdi6p9rmt7li">
      {label && <p className="text-sm font-semibold text-slate-600 text-center" data-id="element-13186" data-mp="1hbdvs7vbf8bq">
          {label}
        </p>}

      {/* Circle SVG */}
      <motion.svg width={svgSize} height={svgSize} viewBox={`0 0 ${svgSize} ${svgSize}`} initial={{
      opacity: 0,
      scale: 0.9
    }} animate={{
      opacity: 1,
      scale: 1
    }} transition={{
      duration: 0.4
    }} className="drop-shadow-md" role="img" aria-label={hasSector ? `Cirkel met sector van ${sectorAngle}°` : 'Cirkel'} data-id="element-13187" data-mp="1r3a4ecv12gu7">
        
        {/* Full circle background */}
        <motion.circle cx={cx} cy={cy} r={circleRadius} fill={hasSector ? 'rgb(241, 245, 249)' : 'rgb(191, 219, 254)'} stroke="rgb(59, 130, 246)" strokeWidth="3" initial={{
        pathLength: 0
      }} animate={{
        pathLength: 1
      }} transition={{
        duration: 1,
        ease: 'easeInOut'
      }} data-id="element-13188" data-mp="18cse0exo3xc0" />
        

        {/* Sector wedge (when sectorAngle is provided) */}
        {hasSector && <motion.path d={sectorPath(cx, cy, circleRadius, sectorAngle)} fill="rgb(147, 197, 253)" stroke="rgb(59, 130, 246)" strokeWidth="2.5" strokeLinejoin="round" initial={{
        opacity: 0,
        scale: 0.8
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        delay: 0.3,
        duration: 0.5,
        ease: 'easeOut'
      }} style={{
        transformOrigin: `${cx}px ${cy}px`
      }} data-id="element-13189" data-mp="3593sa143gxf" />}

        {/* Sector angle arc indicator */}
        {hasSector && sectorAngle <= 180 && (() => {
        const arcR = circleRadius * 0.25;
        const startRad = -Math.PI / 2;
        const endRad = startRad + sectorAngle * Math.PI / 180;
        const largeArc = sectorAngle > 180 ? 1 : 0;
        const ax1 = cx + arcR * Math.cos(startRad);
        const ay1 = cy + arcR * Math.sin(startRad);
        const ax2 = cx + arcR * Math.cos(endRad);
        const ay2 = cy + arcR * Math.sin(endRad);
        return <path d={`M ${ax1} ${ay1} A ${arcR} ${arcR} 0 ${largeArc} 1 ${ax2} ${ay2}`} fill="none" stroke="rgb(99, 102, 241)" strokeWidth="2" strokeDasharray="4,3" data-id="element-13190" data-mp="1cwdhnrs3tj4f" />;
      })()}

        {/* Sector angle label */}
        {hasSector && (() => {
        const labelR = circleRadius * 0.35;
        const midAngleRad = -Math.PI / 2 + sectorAngle * Math.PI / 360;
        const lx = cx + labelR * Math.cos(midAngleRad);
        const ly = cy + labelR * Math.sin(midAngleRad);
        return <text x={lx} y={ly} textAnchor="middle" dominantBaseline="central" className="text-sm font-bold fill-indigo-700" data-id="element-13191" data-mp="yx4djqh6afgt">
              
                {sectorAngle}°
              </text>;
      })()}

        {/* Center point */}
        <circle cx={cx} cy={cy} r="4" fill="rgb(59, 130, 246)" data-id="element-13192" data-mp="p6j6rfrci4mt" />

        {/* Radius line (if highlighted) */}
        {highlightRadius && <>
            <motion.line x1={cx} y1={cy} x2={cx + circleRadius} y2={cy} stroke="rgb(239, 68, 68)" strokeWidth="3" initial={{
          pathLength: 0
        }} animate={{
          pathLength: 1
        }} transition={{
          delay: 0.5,
          duration: 0.6
        }} data-id="element-13193" data-mp="f8liis9hk7u7" />
          
            <text x={cx + circleRadius / 2} y={cy - 10} textAnchor="middle" className="text-sm font-bold fill-red-600" data-id="element-13194" data-mp="5k7v77t04h97">
            
              r = {radius}
            </text>
          </>}

        {/* Diameter line (if highlighted) */}
        {highlightDiameter && <>
            <motion.line x1={cx - circleRadius} y1={cy} x2={cx + circleRadius} y2={cy} stroke="rgb(34, 197, 94)" strokeWidth="3" initial={{
          pathLength: 0
        }} animate={{
          pathLength: 1
        }} transition={{
          delay: 0.5,
          duration: 0.6
        }} data-id="element-13195" data-mp="fgj7vywi7946" />
          
            <text x={cx} y={cy + 25} textAnchor="middle" className="text-sm font-bold fill-green-600" data-id="element-13196" data-mp="kbq7auiqprna">
            
              d = {diameter}
            </text>
          </>}
      </motion.svg>

      {/* Formula display */}
      {mode === 'support' && showFormula && <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4 space-y-4 max-w-md w-full" data-id="element-13197" data-mp="3n3bi1u165vz7">
          {circumference !== undefined && <div className="flex flex-col gap-1" data-id="element-13198" data-mp="3r199l3iewu5r">
              <span className="text-sm font-semibold text-blue-900 border-b border-blue-200 pb-1 mb-1" data-id="element-13199" data-mp="3w1nisovr2a5d">
                Omtrek (C)
              </span>
              <div className="flex justify-between items-center" data-id="element-13200" data-mp="4pviqhmf48sw">
                <span className="text-sm text-blue-800" data-id="element-13201" data-mp="9mlyf3ub92ds">Formule:</span>
                <span className="font-mono font-bold text-blue-700" data-id="element-13202" data-mp="elfcoyhf8xf0">
                  C = 2πr
                </span>
              </div>
              <div className="flex justify-between items-center" data-id="element-13203" data-mp="iolglzh2acib">
                <span className="text-sm text-blue-800" data-id="element-13204" data-mp="nlu4mgow5arh">Invullen:</span>
                <span className="font-mono font-bold text-blue-700" data-id="element-13205" data-mp="2kff3l7tdpw0c">
                  2 × π × {radius}
                </span>
              </div>
              <div className="flex justify-between items-center bg-blue-100 p-2 rounded-md mt-1" data-id="element-13206" data-mp="2perzsj654ffl">
                <span className="text-sm font-bold text-blue-900" data-id="element-13207" data-mp="2hwbo71em7vwj">
                  Uitkomst:
                </span>
                <span className="font-mono font-black text-blue-800" data-id="element-13208" data-mp="2d1xpoq4np8nn">
                  ≈ {circumference.toFixed(1)} cm
                </span>
              </div>
            </div>}
          {area !== undefined && <div className="flex flex-col gap-1" data-id="element-13209" data-mp="233rraii26knj">
              <span className="text-sm font-semibold text-blue-900 border-b border-blue-200 pb-1 mb-1" data-id="element-13210" data-mp="30tv32lnr88qv">
                Oppervlakte (A)
              </span>
              <div className="flex justify-between items-center" data-id="element-13211" data-mp="2vsp8sjeg4df8">
                <span className="text-sm text-blue-800" data-id="element-13212" data-mp="2qtudihzakwe8">Formule:</span>
                <span className="font-mono font-bold text-blue-700" data-id="element-13213" data-mp="2muljrd494yio">
                  A = πr²
                </span>
              </div>
              <div className="flex justify-between items-center" data-id="element-13214" data-mp="1fhl8d173jkij">
                <span className="text-sm text-blue-800" data-id="element-13215" data-mp="1bdlvcsl0z3tx">Invullen:</span>
                <span className="font-mono font-bold text-blue-700" data-id="element-13216" data-mp="3ajolmu8wnacj">
                  π × {radius}²
                </span>
              </div>
              <div className="flex justify-between items-center bg-blue-100 p-2 rounded-md mt-1" data-id="element-13217" data-mp="3fjb3jgzohero">
                <span className="text-sm font-bold text-blue-900" data-id="element-13218" data-mp="31k1tkz22fgw5">
                  Uitkomst:
                </span>
                <span className="font-mono font-black text-blue-800" data-id="element-13219" data-mp="36i3elpj1xqa9">
                  ≈ {area.toFixed(1)} cm²
                </span>
              </div>
            </div>}
          {!circumference && !area && <div className="space-y-1" data-id="element-13220" data-mp="22ts45dzz2b4">
              <p className="text-sm text-blue-900" data-id="element-13221" data-mp="3khur1ltx6ym5">
                <span className="font-bold" data-id="element-13222" data-mp="3khcjht1t4xyx">Omtrek:</span> C = 2πr of C = πd
              </p>
              <p className="text-sm text-blue-900" data-id="element-13223" data-mp="3pf4mpj7wk6if">
                <span className="font-bold" data-id="element-13224" data-mp="h0cgv7qodfik">Oppervlakte:</span> A = πr²
              </p>
            </div>}
        </div>}

      {/* Sector info card (when sector is shown) */}
      {hasSector && mode === 'support' && <div className="flex items-center gap-3 bg-indigo-50 border border-indigo-200 rounded-xl px-5 py-3 shadow-sm" data-id="element-13225" data-mp="v5we4s1up0pp">
          <div className="flex flex-col gap-1" data-id="element-13226" data-mp="r3rgzjc71lyb">
            <div className="flex items-center gap-2" data-id="element-13227" data-mp="r2zvx2g836ma">
              <span className="text-sm font-semibold text-indigo-900" data-id="element-13228" data-mp="14uwdick9ly4f">
                Sector:
              </span>
              <span className="text-lg font-black text-indigo-700" data-id="element-13229" data-mp="zxwgss9b27pz">
                {sectorAngle}°
              </span>
            </div>
            <div className="flex items-center gap-2" data-id="element-13230" data-mp="8687keltq1bd">
              <span className="text-sm font-semibold text-indigo-900" data-id="element-13231" data-mp="87jbqfj6lb30">
                Deel:
              </span>
              <span className="text-lg font-black text-indigo-700" data-id="element-13232" data-mp="m3n0vuf76t8m">
                {sectorAngle}/360
              </span>
            </div>
          </div>
        </div>}

      {/* Measurements card */}
      {mode === 'support' && (propRadius || propDiameter) && !hasSector && <div className="flex items-center gap-3 bg-indigo-50 border border-indigo-200 rounded-xl px-5 py-3 shadow-sm" data-id="element-13233" data-mp="1sl0eec62jjrq">
          <div className="flex flex-col gap-1" data-id="element-13234" data-mp="1o971ov5052xo">
            {propRadius && <div className="flex items-center gap-2" data-id="element-13235" data-mp="1jam1gvpr2j98">
                <span className="text-sm font-semibold text-indigo-900" data-id="element-13236" data-mp="1jd890vcn4yrg">
                  Straal (r):
                </span>
                <span className="text-lg font-black text-indigo-700" data-id="element-13237" data-mp="19llzr4jv59rc">
                  {radius} cm
                </span>
              </div>}
            {propDiameter && <div className="flex items-center gap-2" data-id="element-13238" data-mp="15he8p0pxnhpy">
                <span className="text-sm font-semibold text-indigo-900" data-id="element-13239" data-mp="10k4k1nadba36">
                  Diameter (d):
                </span>
                <span className="text-lg font-black text-indigo-700" data-id="element-13240" data-mp="vltaldnzj1lh">
                  {diameter} cm
                </span>
              </div>}
          </div>
        </div>}
    </div>;
}